import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Settings, RefreshCw, Calculator, AlertTriangle, CheckCircle2,
  Mail, Eye, X as XIcon, Plus, ShieldAlert, Send, Info,
} from "lucide-react";

function YearRangePreview({ axiosAuth, dep }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axiosAuth().get("/rrhh/year-range");
        if (!cancel) setInfo(res.data);
      } catch {
        // silencioso: solo es vista previa
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dep || []);
  if (!info) return null;
  return (
    <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="rrhh-year-range-preview">
      <strong>Vista actual:</strong>{" "}
      <span className="font-mono">{info.start_year} … {info.end_year}</span>{" "}
      <span className="text-slate-400">({info.years?.length || 0} años)</span>
      <p className="text-[11px] text-slate-400 mt-1">Guarda los cambios para aplicarlo a los selectores RRHH.</p>
    </div>
  );
}

const MODES = [
  { value: "natural_days", label: "Descuenta días naturales (incluye fines de semana y festivos)" },
  { value: "business_days", label: "Descuenta solo laborables (L–V, excluye festivos configurados)" },
  { value: "manual", label: "Manual — no calcula automáticamente" },
];

const VACATION_MODES = MODES.filter((m) => m.value !== "manual");
const SICK_MODES = MODES.filter((m) => m.value !== "manual");

const FIELDS = [
  { key: "vacation_day_calculation_mode", label: "Vacaciones", options: VACATION_MODES, hint: "Default Ascauto: Descuenta solo laborables." },
  { key: "personal_days_calculation_mode", label: "Asuntos propios", options: MODES, hint: "Default Ascauto: Descuenta solo laborables." },
  { key: "paid_leave_calculation_mode", label: "Permisos retribuidos", options: MODES, hint: "Default Ascauto: Manual." },
  { key: "sick_leave_calculation_mode", label: "Baja médica", options: SICK_MODES, hint: "Default Ascauto: Días naturales." },
];

const REQUEST_TYPE_PREVIEW = [
  { type: "vacaciones", label: "Vacaciones" },
  { type: "asuntos_propios", label: "Asuntos propios" },
  { type: "permiso", label: "Permiso retribuido" },
  { type: "baja_medica", label: "Baja médica" },
];

const PORTAL_TYPES = [
  { key: "vacaciones", label: "Vacaciones" },
  { key: "asuntos_propios", label: "Asuntos propios" },
  { key: "permiso_retribuido", label: "Permiso retribuido" },
  { key: "baja_medica", label: "Baja médica" },
  { key: "ausencia_puntual", label: "Ausencia puntual" },
  { key: "otro", label: "Otro" },
];

const VIS_FLAGS = [
  { key: "card", label: "Tarjeta en dashboard" },
  { key: "allow_request", label: "Permitir solicitud" },
  { key: "summary", label: "Resumen superior" },
  { key: "filter", label: "Filtros 'Mis solicitudes'" },
];

// ── Iter 186 (Parte 2) — Destinatarios RRHH ──
const HR_EVENTS = [
  { key: "solicitud_creada", label: "Solicitud creada", critical: true,
    hint: "Se dispara cuando un empleado envía una solicitud nueva." },
  { key: "paso_aprobado", label: "Paso aprobado", critical: false,
    hint: "Avanza un paso de la cadena de aprobación." },
  { key: "aprobada_final", label: "Aprobada final", critical: false,
    hint: "La solicitud queda definitivamente aprobada." },
  { key: "rechazada", label: "Rechazada", critical: false,
    hint: "Algún aprobador rechaza la solicitud." },
  { key: "baja_comunicada", label: "Baja médica comunicada", critical: true,
    hint: "El empleado o RRHH registra una baja médica." },
  { key: "doc_pendiente", label: "Documentación pendiente", critical: false,
    hint: "Recordatorio de doc faltante (parte de baja, etc)." },
];

const TO_OPTIONS = [
  { value: "hr", label: "RRHH (principal + extras)" },
  { value: "employee", label: "Empleado de la solicitud" },
  { value: "manager", label: "Mánager directo (o gerente configurado)" },
  { value: "next_approver", label: "Siguiente aprobador (jefe directo)" },
  { value: "admin", label: "Admin (auto, excluye sistema)" },
];

const BANNED_OPERATIONAL_EMAILS = ["admin@taller.com"];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isBannedEmail = (e) => !!e && BANNED_OPERATIONAL_EMAILS.includes(String(e).trim().toLowerCase());
const isValidEmail = (e) => !!e && EMAIL_RE.test(String(e).trim());

export default function RrhhConfigPage() {
  const { axiosAuth, user, isSuperAdmin, hasPermission } = useAuth();
  const [config, setConfig] = useState(null);
  const [savedConfig, setSavedConfig] = useState(null); // Iter 208: copia última guardada para detectar 'dirty'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [previewStart, setPreviewStart] = useState(() => {
    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() + 7);
    return start.toISOString().slice(0, 10);
  });
  const [previewEnd, setPreviewEnd] = useState(() => {
    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() + 7);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return end.toISOString().slice(0, 10);
  });
  const [previews, setPreviews] = useState([]);
  // Iter 186 (Parte 2) — Destinatarios RRHH
  const [extraInput, setExtraInput] = useState("");
  const [recipientsDialog, setRecipientsDialog] = useState({ open: false, data: null, loading: false });
  const [previewedOnce, setPreviewedOnce] = useState(false);
  // Iter 186 (Parte 2 - extensión) — Test email
  const [testEventKey, setTestEventKey] = useState("solicitud_creada");
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Default dates for preview: a week from today (inicialización en useState evita set-state-in-effect)
  const _defaultRange = useMemo(() => {
    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() + 7);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { start: iso(start), end: iso(end) };
  }, []);

  const canEdit = isSuperAdmin || hasPermission("configuracion", "editar");

  const load = async () => {
    try {
      setLoading(true);
      const res = await axiosAuth().get("/rrhh/config");
      // Iter 186 (Parte 2) — hidratar defaults para destinatarios RRHH
      // (el endpoint solo trae los campos guardados en db.rrhh_config; los de
      // db.hr_config se gestionan aparte vía PUT /api/rrhh/config).
      const data = {
        hr_email_enabled: true,
        hr_main_email: "",
        hr_extra_emails: [],
        manager_email: "",
        fallback_email: "",
        hr_email_events: {},
        ...res.data,
      };
      // Hidratar eventos por defecto si faltan
      const DEFAULT_EVENTS = {
        solicitud_creada: { enabled: true, to: "hr", subject: "Nueva solicitud de {type} - {employee}" },
        paso_aprobado: { enabled: true, to: "next_approver", subject: "Paso aprobado: {type} - {employee}" },
        aprobada_final: { enabled: true, to: "employee", subject: "Tu solicitud de {type} ha sido aprobada" },
        rechazada: { enabled: true, to: "employee", subject: "Tu solicitud de {type} ha sido rechazada" },
        baja_comunicada: { enabled: true, to: "hr", subject: "Baja medica comunicada - {employee}" },
        doc_pendiente: { enabled: true, to: "employee", subject: "Documentacion pendiente - {employee}" },
      };
      const events = { ...DEFAULT_EVENTS };
      for (const [k, v] of Object.entries(data.hr_email_events || {})) {
        events[k] = { ...DEFAULT_EVENTS[k], ...v };
      }
      data.hr_email_events = events;
      setConfig(data);
      setSavedConfig(data); // Iter 208: snapshot inicial post-load = última versión guardada
    } catch (e) {
      toast.error("No se pudo cargar la configuración de RRHH");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  // Default dates for preview se inicializan en los useState (evita set-state-in-effect)

  const update = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const save = async () => {
    if (!canEdit) return;
    // Iter 186 (Parte 2) — Vista previa obligatoria si hay cambios en destinatarios RRHH
    if (!previewedOnce) {
      toast.warning("Abre la 'Vista previa de destinatarios' al menos una vez antes de guardar.");
      return;
    }
    // Validar emails (cliente)
    const opEmails = [
      [config.hr_main_email, "RRHH principal"],
      [config.manager_email, "Gerente"],
      [config.fallback_email, "Fallback"],
      ...(config.hr_extra_emails || []).map((e, i) => [e, `Extra ${i + 1}`]),
    ];
    for (const [e, label] of opEmails) {
      if (!e) continue;
      if (!isValidEmail(e)) {
        toast.error(`${label}: email no válido (${e})`);
        return;
      }
      if (isBannedEmail(e)) {
        toast.error(`${label}: ${e} es cuenta del sistema y NO puede recibir correos operativos.`);
        return;
      }
    }
    try {
      setSaving(true);
      const payload = {};
      for (const f of FIELDS) payload[f.key] = config[f.key];
      // Año range
      if (config.year_range_start_year === "" || config.year_range_start_year === null) {
        payload.year_range_start_year = null;
      } else if (config.year_range_start_year != null) {
        payload.year_range_start_year = parseInt(config.year_range_start_year, 10);
      }
      if (config.year_range_future_years != null && config.year_range_future_years !== "") {
        payload.year_range_future_years = parseInt(config.year_range_future_years, 10);
      }
      // Portal visibility (envío completo)
      if (config.portal_visibility) payload.portal_visibility = config.portal_visibility;
      // Iter 186 (Parte 2) — Destinatarios RRHH
      payload.hr_email_enabled = !!config.hr_email_enabled;
      payload.hr_main_email = (config.hr_main_email || "").trim();
      payload.hr_extra_emails = (config.hr_extra_emails || []).map((e) => (e || "").trim()).filter(Boolean);
      payload.manager_email = (config.manager_email || "").trim();
      payload.fallback_email = (config.fallback_email || "").trim();
      if (config.hr_email_events) payload.hr_email_events = config.hr_email_events;
      const res = await axiosAuth().put("/rrhh/config", payload);
      // Hidratar la respuesta del PUT con los DEFAULT_EVENTS y campos de email
      // (el doc devuelto puede no incluir todos los eventos por defecto).
      const merged = { ...config, ...res.data };
      setConfig(merged);
      setSavedConfig(merged); // Iter 208: sincronizar baseline tras guardar
      setPreviewedOnce(false); // Se vuelve a exigir tras cambios futuros
      toast.success("Configuración actualizada");
      // Iter 208: re-ejecutar preview si el usuario ya lo había lanzado para
      // mostrar el resultado con la nueva config sin tener que repulsar "Calcular".
      if (previews.length > 0) {
        // pequeño delay para evitar pisar el setConfig anterior antes del refetch
        setTimeout(() => { runPreview(); }, 50);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const setVis = (type, flag, value) => {
    setConfig((c) => ({
      ...c,
      portal_visibility: {
        ...(c.portal_visibility || {}),
        [type]: {
          ...((c.portal_visibility || {})[type] || { card: true, allow_request: true, summary: true, filter: true }),
          [flag]: value,
        },
      },
    }));
  };

  // ── Iter 186 (Parte 2) — Helpers destinatarios RRHH ──
  const setEvent = (key, patch) => setConfig((c) => {
    const events = { ...(c.hr_email_events || {}) };
    const cur = events[key] || { enabled: true, to: "hr", subject: "" };
    events[key] = { ...cur, ...patch };
    return { ...c, hr_email_events: events };
  });

  // ── Iter 186 (Parte 2 - extensión) — Envío de email de prueba a mí mismo ──
  const sendTestEmail = async () => {
    if (!canEdit) return;
    const userEmail = (user?.email || "").trim();
    if (!userEmail) {
      toast.error("Tu cuenta no tiene un email en perfil; no se puede enviar la prueba.");
      return;
    }
    try {
      setSendingTest(true);
      setTestResult(null);
      const proposed = {
        hr_email_enabled: !!config.hr_email_enabled,
        hr_main_email: (config.hr_main_email || "").trim(),
        hr_extra_emails: (config.hr_extra_emails || []).map((e) => e.trim()).filter(Boolean),
        manager_email: (config.manager_email || "").trim(),
        fallback_email: (config.fallback_email || "").trim(),
        hr_email_events: config.hr_email_events || {},
      };
      const res = await axiosAuth().post("/rrhh/config/send-test-email", {
        event_key: testEventKey,
        proposed_config: proposed,
      });
      setTestResult(res.data);
      if (res.data.ok) {
        toast.success(`Email de prueba enviado a ${res.data.sent_to}`);
      } else {
        toast.warning("Plantilla generada pero el proveedor devolvió aviso. Revisa el detalle.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar el email de prueba");
    } finally {
      setSendingTest(false);
    }
  };


  const addExtra = () => {
    const val = (extraInput || "").trim();
    if (!val) return;
    if (!isValidEmail(val)) {
      toast.warning("Email no válido");
      return;
    }
    if (isBannedEmail(val)) {
      toast.error(`${val} es cuenta del sistema y NO puede recibir correos operativos.`);
      return;
    }
    const list = config.hr_extra_emails || [];
    if (list.map((e) => e.toLowerCase()).includes(val.toLowerCase())) {
      toast.info("Ese email ya está en la lista");
      return;
    }
    setConfig((c) => ({ ...c, hr_extra_emails: [...(c.hr_extra_emails || []), val] }));
    setExtraInput("");
    setPreviewedOnce(false);
  };

  const removeExtra = (idx) => {
    setConfig((c) => ({
      ...c,
      hr_extra_emails: (c.hr_extra_emails || []).filter((_, i) => i !== idx),
    }));
    setPreviewedOnce(false);
  };

  const onEmailChange = (key, val) => {
    setConfig((c) => ({ ...c, [key]: val }));
    setPreviewedOnce(false);
  };

  const openRecipientsPreview = async () => {
    if (!canEdit) return;
    // Validación previa de emails sospechosos antes de llamar al backend
    const opEmails = [
      [config.hr_main_email, "RRHH principal"],
      [config.manager_email, "Gerente"],
      [config.fallback_email, "Fallback"],
      ...(config.hr_extra_emails || []).map((e, i) => [e, `Extra ${i + 1}`]),
    ];
    for (const [e, label] of opEmails) {
      if (!e) continue;
      if (!isValidEmail(e)) {
        toast.error(`${label}: email no válido (${e})`);
        return;
      }
      if (isBannedEmail(e)) {
        toast.error(`${label}: ${e} es cuenta del sistema y NO puede ser destinatario.`);
        return;
      }
    }
    setRecipientsDialog({ open: true, data: null, loading: true });
    try {
      const proposed = {
        hr_email_enabled: !!config.hr_email_enabled,
        hr_main_email: (config.hr_main_email || "").trim(),
        hr_extra_emails: (config.hr_extra_emails || []).map((e) => e.trim()).filter(Boolean),
        manager_email: (config.manager_email || "").trim(),
        fallback_email: (config.fallback_email || "").trim(),
        hr_email_events: config.hr_email_events || {},
      };
      const res = await axiosAuth().post("/rrhh/config/preview-recipients", { proposed_config: proposed });
      setRecipientsDialog({ open: true, data: res.data, loading: false });
      setPreviewedOnce(true);
    } catch (e) {
      setRecipientsDialog({ open: false, data: null, loading: false });
      toast.error(e.response?.data?.detail || "No se pudo generar la vista previa");
    }
  };

  const runBackfill = async () => {
    if (!canEdit) return;
    if (!window.confirm("Esto añadirá `calculation_mode` a solicitudes antiguas que no lo tengan.\n\nNO recalcula ni modifica los días ya guardados. ¿Continuar?")) return;
    try {
      setBackfilling(true);
      const res = await axiosAuth().post("/rrhh/config/backfill-calculation-mode");
      toast.success(`Migración OK · ${res.data.backfilled} solicitudes etiquetadas (días intactos)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en la migración");
    } finally {
      setBackfilling(false);
    }
  };

  const runPreview = async () => {
    if (!previewStart || !previewEnd) {
      toast.warning("Indica fecha de inicio y fin");
      return;
    }
    try {
      const results = await Promise.all(REQUEST_TYPE_PREVIEW.map(async (rt) => {
        try {
          const res = await axiosAuth().get(`/rrhh/calculate-days?request_type=${rt.type}&start_date=${previewStart}&end_date=${previewEnd}`);
          return { ...rt, ...res.data };
        } catch (e) {
          return { ...rt, error: e.response?.data?.detail || "error" };
        }
      }));
      setPreviews(results);
    } catch {
      toast.error("Error en preview");
    }
  };

  // Iter 208: detecta si los modos de cálculo en pantalla difieren de los
  // guardados (savedConfig). Esto evita que el preview muestre badges que no
  // se han persistido aún en BD.
  const modesDirty = useMemo(() => {
    if (!config || !savedConfig) return false;
    return FIELDS.some((f) => config[f.key] !== savedConfig[f.key]);
  }, [config, savedConfig]);

  const modeBadge = (m) => {
    const styles = {
      natural_days: "bg-blue-100 text-blue-700 border-blue-200",
      business_days: "bg-emerald-100 text-emerald-700 border-emerald-200",
      manual: "bg-amber-100 text-amber-700 border-amber-200",
    };
    const labels = {
      natural_days: "Descuento natural",
      business_days: "Descuento laborable",
      manual: "Manual",
    };
    return (
      <span
        className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${styles[m] || "bg-slate-100 text-slate-700"}`}
        data-testid={`mode-badge-${m}`}
      >
        {labels[m] || m}
      </span>
    );
  };

  if (loading) return <div className="p-8 text-sm text-slate-500">Cargando configuración…</div>;
  if (!config) return <div className="p-8 text-sm text-red-600">No se ha podido cargar la configuración.</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="rrhh-config-page">
      {/* Header */}
      <div className="flex items-start gap-3 border-b border-slate-200 pb-4">
        <Settings className="w-6 h-6 text-slate-500 mt-1" />
        <div>
          <h1 className="text-2xl font-bold heading-industrial uppercase text-slate-900">Configuración de cálculo · RRHH</h1>
          <p className="text-sm text-slate-500 mt-1">Define cómo se cuentan los días consumidos para cada tipo de solicitud. Los cambios <strong>no</strong> afectan a solicitudes ya creadas (cada una guarda el método con el que fue calculada).</p>
        </div>
      </div>

      {/* Iter 218 — Banner aclaratorio (inverso al de "RRHH > Reglas") */}
      <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex gap-2" data-testid="rrhh-config-scope-banner">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Esta pantalla define <strong>cómo se descuentan los días</strong> (laborables / naturales / manual) y es la <strong>única fuente de verdad</strong> usada por el portal del empleado, el modal &quot;Nueva solicitud por empleado&quot; y los listados. Los <strong>límites de saldo, bloqueos y aprobadores</strong> se gestionan en <strong>RRHH &gt; Reglas</strong>.</span>
      </div>

      {!canEdit && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2" data-testid="rrhh-config-readonly-banner">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Solo lectura. Necesitas ser <strong>super_admin</strong> o tener el permiso <code className="text-[11px]">configuracion/editar</code> para modificar estos valores.</span>
        </div>
      )}

      {/* Form */}
      <Card className="p-6 space-y-5 rounded-sm">
        <div className="grid md:grid-cols-2 gap-5">
          {FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">{f.label}</label>
              <select
                value={config[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                disabled={!canEdit}
                data-testid={`rrhh-config-${f.key}`}
                className="w-full text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500"
              >
                {f.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400">{f.hint}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-100">
          <Button onClick={save} disabled={!canEdit || saving} data-testid="rrhh-config-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm">
            {saving ? "Guardando…" : "Guardar configuración"}
          </Button>
          <Button onClick={runBackfill} disabled={!canEdit || backfilling} variant="outline" data-testid="rrhh-config-backfill-btn" className="rounded-sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            {backfilling ? "Migrando…" : "Etiquetar solicitudes antiguas (idempotente)"}
          </Button>
          <span className="text-[11px] text-slate-400">No modifica días guardados — solo añade <code>calculation_mode</code> a las que no lo tengan.</span>
        </div>
      </Card>

      {/* Preview */}
      <Card className="p-6 space-y-4 rounded-sm">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">Previsualización</h2>
        </div>
        {modesDirty && (
          <div
            className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2"
            data-testid="rrhh-preview-dirty-warning"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Has cambiado el método de cálculo arriba pero <strong>aún no se ha guardado</strong>.
              La previsualización siempre usa la configuración guardada en base de datos —{" "}
              <strong>guarda la configuración</strong> para que el cálculo refleje los nuevos modos.
            </span>
          </div>
        )}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1">Inicio</label>
            <input type="date" value={previewStart} onChange={(e) => setPreviewStart(e.target.value)} data-testid="rrhh-preview-start" className="text-sm rounded-sm border border-slate-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-600 mb-1">Fin</label>
            <input type="date" value={previewEnd} onChange={(e) => setPreviewEnd(e.target.value)} data-testid="rrhh-preview-end" className="text-sm rounded-sm border border-slate-300 px-3 py-2" />
          </div>
          <Button onClick={runPreview} variant="outline" data-testid="rrhh-preview-run" className="rounded-sm">Calcular</Button>
        </div>
        {previews.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="rrhh-preview-results">
            {previews.map((p) => {
              const naturalP = p.natural_days_in_period;
              const holsExcluded = Array.isArray(p.holidays_excluded) ? p.holidays_excluded : [];
              const weekendsExcluded = (p.mode === "business_days" && naturalP != null && p.days != null)
                ? Math.max(0, naturalP - p.days - holsExcluded.length)
                : 0;
              const unit = p.mode === "business_days" ? "laborables" : p.mode === "natural_days" ? "naturales" : "días";
              return (
                <div
                  key={p.type}
                  className="rounded-sm border border-slate-200 p-3 bg-white"
                  data-testid={`rrhh-preview-card-${p.type}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{p.label}</span>
                    {!p.error && modeBadge(p.mode)}
                  </div>
                  {p.error ? (
                    <p className="text-xs text-red-600 mt-1">{p.error}</p>
                  ) : (
                    <>
                      <p
                        className="text-3xl font-extrabold text-slate-900 mt-2 leading-none"
                        data-testid={`rrhh-preview-discounted-${p.type}`}
                      >
                        {p.days}
                        <span className="text-xs font-semibold text-slate-500 ml-1.5">{unit} descontados</span>
                      </p>
                      <ul className="mt-2 space-y-0.5 text-[11px] text-slate-600">
                        {naturalP != null && (
                          <li>
                            Periodo natural: <span className="font-mono font-semibold">{naturalP}</span> días
                          </li>
                        )}
                        <li>
                          Modo aplicado:{" "}
                          <span className="font-semibold">
                            {p.mode === "business_days" ? "descuento laborable" : p.mode === "natural_days" ? "días naturales" : "manual"}
                          </span>
                        </li>
                        {holsExcluded.length > 0 && (
                          <li className="text-amber-700">
                            Festivos excluidos: <span className="font-semibold">{holsExcluded.length}</span>
                          </li>
                        )}
                        {weekendsExcluded > 0 && (
                          <li className="text-amber-700">
                            Fines de semana excluidos: <span className="font-semibold">{weekendsExcluded}</span>
                          </li>
                        )}
                      </ul>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Año range */}
      <Card className="p-6 space-y-4 rounded-sm" data-testid="rrhh-year-range-card">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">Rango de años visibles</h2>
        </div>
        <p className="text-xs text-slate-500">
          Define el rango de años que aparecerá en los selectores de RRHH (festivos, filtros, calendario, balances).
          Por defecto: desde <strong>año actual − 2</strong> hasta <strong>año actual + 10</strong>.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">Año inicial fijo (opcional)</label>
            <input
              type="number" min="2000" max="2100"
              value={config.year_range_start_year ?? ""}
              placeholder={`${new Date().getFullYear() - 2} (auto)`}
              onChange={(e) => update("year_range_start_year", e.target.value === "" ? null : parseInt(e.target.value, 10))}
              disabled={!canEdit}
              data-testid="rrhh-year-start"
              className="w-full text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500"
            />
            <p className="text-[11px] text-slate-400">Vacío = automático (año actual − 2). Ej: 2024.</p>
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">Años futuros visibles</label>
            <input
              type="number" min="0" max="30"
              value={config.year_range_future_years ?? 10}
              onChange={(e) => update("year_range_future_years", parseInt(e.target.value, 10))}
              disabled={!canEdit}
              data-testid="rrhh-year-future"
              className="w-full text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500"
            />
            <p className="text-[11px] text-slate-400">Ej: 10 → hasta {(new Date().getFullYear()) + (parseInt(config.year_range_future_years, 10) || 10)}.</p>
          </div>
        </div>
        <YearRangePreview axiosAuth={axiosAuth} dep={[config.year_range_start_year, config.year_range_future_years]} />
        <p className="text-[11px] text-slate-400">No se generan datos masivos por adelantado: los balances anuales se crean bajo demanda al usarse.</p>
      </Card>

      {/* Portal visibility */}
      <Card className="p-6 space-y-4 rounded-sm" data-testid="rrhh-portal-visibility-card">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-slate-500" />
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">Visibilidad Portal Empleado</h2>
        </div>
        <p className="text-xs text-slate-500">
          Controla qué bloques ven los empleados en su portal y qué tipos pueden solicitar.
          <strong> Ocultar una tarjeta no impide que el empleado pueda solicitar ese tipo</strong> si está activado como &quot;permitir solicitud&quot;.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="portal-visibility-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600">Tipo</th>
                {VIS_FLAGS.map((f) => (
                  <th key={f.key} className="px-3 py-2 text-center text-[11px] uppercase tracking-wider font-bold text-slate-600">{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PORTAL_TYPES.map((t) => {
                const v = (config.portal_visibility || {})[t.key] || { card: true, allow_request: true, summary: true, filter: true };
                return (
                  <tr key={t.key} className="border-b border-slate-100" data-testid={`portal-vis-row-${t.key}`}>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{t.label}</td>
                    {VIS_FLAGS.map((f) => (
                      <td key={f.key} className="px-3 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={v[f.key] !== false}
                          disabled={!canEdit}
                          onChange={(e) => setVis(t.key, f.key, e.target.checked)}
                          data-testid={`portal-vis-${t.key}-${f.key}`}
                          className="h-4 w-4 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-900 disabled:opacity-50"
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 flex gap-2">
          <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>RRHH y administradores siguen viendo <strong>todos</strong> los tipos para gestión. Esta configuración solo afecta a la vista del Portal Empleado.</span>
        </div>
      </Card>

      {/* ── Iter 186 (Parte 2) — Destinatarios de notificaciones RRHH ── */}
      <Card className="p-6 space-y-5 rounded-sm border-l-4 border-l-blue-500" data-testid="rrhh-recipients-card">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">
              Destinatarios de notificaciones RRHH
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Configura quién recibe los emails operativos de RRHH. La cuenta bootstrap
              {" "}<code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded-sm">admin@taller.com</code>{" "}
              y cualquier usuario con <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded-sm">is_system_exception=true</code>{" "}
              quedan SIEMPRE excluidas a nivel backend.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0" data-testid="hr-email-enabled-toggle-wrap">
            <Switch
              checked={!!config.hr_email_enabled}
              onCheckedChange={(v) => { onEmailChange("hr_email_enabled", v); }}
              disabled={!canEdit}
              data-testid="hr-email-enabled-switch"
            />
            <span className="text-xs font-semibold text-slate-700">
              {config.hr_email_enabled ? "Activadas" : "Desactivadas"}
            </span>
          </div>
        </div>

        {/* Actores base */}
        <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
              RRHH principal <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={config.hr_main_email || ""}
              onChange={(e) => onEmailChange("hr_main_email", e.target.value)}
              placeholder="rrhh@ascauto.com"
              disabled={!canEdit}
              data-testid="hr-main-email-input"
              className="w-full text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100"
            />
            <p className="text-[11px] text-slate-400">Recibe los eventos cuyo destino es &quot;RRHH&quot;.</p>
            {isBannedEmail(config.hr_main_email) && (
              <p className="text-[11px] text-red-600 flex items-center gap-1">
                <ShieldAlert className="w-3 h-3" /> Cuenta del sistema — no permitida.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
              Gerente
            </label>
            <input
              type="email"
              value={config.manager_email || ""}
              onChange={(e) => onEmailChange("manager_email", e.target.value)}
              placeholder="gerencia@ascauto.com"
              disabled={!canEdit}
              data-testid="manager-email-input"
              className="w-full text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100"
            />
            <p className="text-[11px] text-slate-400">Se usa cuando el empleado no tiene mánager directo.</p>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
              Fallback (último recurso)
            </label>
            <input
              type="email"
              value={config.fallback_email || ""}
              onChange={(e) => onEmailChange("fallback_email", e.target.value)}
              placeholder="operaciones@ascauto.com"
              disabled={!canEdit}
              data-testid="fallback-email-input"
              className="w-full text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100"
            />
            <p className="text-[11px] text-slate-400">
              Si un evento queda sin destinatario resoluble, se usa este buzón. Evita perder notificaciones.
            </p>
          </div>
        </div>

        {/* Extras */}
        <div className="pt-3 border-t border-slate-100 space-y-2" data-testid="hr-extras-wrap">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700">
            Emails adicionales de RRHH (CC)
          </label>
          <div className="flex flex-wrap gap-2 min-h-[2rem]">
            {(config.hr_extra_emails || []).length === 0 && (
              <span className="text-[11px] text-slate-400 italic">Sin emails adicionales.</span>
            )}
            {(config.hr_extra_emails || []).map((em, idx) => (
              <span
                key={`${em}-${idx}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-medium border ${
                  isBannedEmail(em)
                    ? "bg-red-50 border-red-300 text-red-700"
                    : "bg-slate-100 border-slate-200 text-slate-700"
                }`}
                data-testid={`hr-extra-chip-${idx}`}
              >
                {em}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeExtra(idx)}
                    className="hover:bg-slate-300 rounded-sm p-0.5"
                    data-testid={`hr-extra-remove-${idx}`}
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </span>
            ))}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <input
                type="email"
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }}
                placeholder="extra@ascauto.com"
                data-testid="hr-extra-input"
                className="flex-1 text-sm rounded-sm border border-slate-300 px-3 py-2 bg-white focus:border-slate-900 focus:outline-none"
              />
              <Button onClick={addExtra} variant="outline" data-testid="hr-extra-add-btn" className="rounded-sm">
                <Plus className="w-4 h-4 mr-1" /> Añadir
              </Button>
            </div>
          )}
        </div>

        {/* Matriz de eventos */}
        <div className="pt-3 border-t border-slate-100 space-y-2" data-testid="hr-events-matrix">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
            Eventos de RRHH y destinatarios
          </label>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-slate-200 rounded-sm" data-testid="hr-events-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600">Evento</th>
                  <th className="text-center px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600 w-24">Activo</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600">Destinatario</th>
                  <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-slate-600">Asunto</th>
                </tr>
              </thead>
              <tbody>
                {HR_EVENTS.map((evt) => {
                  const ev = (config.hr_email_events || {})[evt.key] || { enabled: true, to: "hr", subject: "" };
                  return (
                    <tr key={evt.key} className="border-b border-slate-100" data-testid={`hr-event-row-${evt.key}`}>
                      <td className="px-3 py-2.5 align-top">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-800">{evt.label}</span>
                          {evt.critical && (
                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-200" title="No puede quedarse sin destinatarios">
                              Crítico
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5">{evt.hint}</p>
                      </td>
                      <td className="px-3 py-2.5 text-center align-top">
                        <Switch
                          checked={ev.enabled !== false}
                          onCheckedChange={(v) => { setEvent(evt.key, { enabled: v }); setPreviewedOnce(false); }}
                          disabled={!canEdit}
                          data-testid={`hr-event-enabled-${evt.key}`}
                        />
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <select
                          value={ev.to || "hr"}
                          onChange={(e) => { setEvent(evt.key, { to: e.target.value }); setPreviewedOnce(false); }}
                          disabled={!canEdit}
                          data-testid={`hr-event-to-${evt.key}`}
                          className="w-full text-xs rounded-sm border border-slate-300 px-2 py-1.5 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100"
                        >
                          {TO_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        <input
                          type="text"
                          value={ev.subject || ""}
                          onChange={(e) => { setEvent(evt.key, { subject: e.target.value }); setPreviewedOnce(false); }}
                          disabled={!canEdit}
                          data-testid={`hr-event-subject-${evt.key}`}
                          placeholder="Asunto del email"
                          className="w-full text-xs rounded-sm border border-slate-300 px-2 py-1.5 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">
                          Variables: {"{employee}"}, {"{type}"}, {"{start_date}"}, {"{end_date}"}, {"{status}"}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-slate-100">
          <Button
            onClick={openRecipientsPreview}
            disabled={!canEdit || recipientsDialog.loading}
            variant="outline"
            data-testid="hr-recipients-preview-btn"
            className="rounded-sm border-blue-500 text-blue-700 hover:bg-blue-50"
          >
            <Eye className="w-4 h-4 mr-2" />
            {recipientsDialog.loading ? "Calculando…" : "Vista previa de destinatarios (obligatoria)"}
          </Button>
          <Button
            onClick={save}
            disabled={!canEdit || saving || !previewedOnce}
            data-testid="hr-recipients-save-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
            title={!previewedOnce ? "Abre la vista previa al menos una vez antes de guardar" : ""}
          >
            {saving ? "Guardando…" : (previewedOnce ? "Guardar destinatarios" : "Guardar (requiere vista previa)")}
          </Button>
          {!previewedOnce && (
            <span className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="hr-preview-required-hint">
              <AlertTriangle className="w-3 h-3" />
              Vista previa obligatoria antes de guardar.
            </span>
          )}
        </div>

        {/* ── Envío de email de prueba a mí mismo ── */}
        <div className="pt-4 border-t border-slate-100 space-y-3 bg-amber-50/40 -mx-6 px-6 pb-2" data-testid="hr-test-email-section">
          <div className="flex items-start gap-2">
            <Send className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                Enviar email de prueba a mí mismo
              </h3>
              <p className="text-[11px] text-slate-500 mt-1">
                Manda la plantilla del evento elegido a <strong>tu propio buzón</strong> ({user?.email || "(sin email)"}).
                Usa la <strong>configuración visible en pantalla</strong> (incluso sin guardar). No crea solicitudes,
                no modifica saldos y no se envía a RRHH ni a empleados reales. Queda registrado en
                {" "}<code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded-sm">email_audit</code>{" "}
                con módulo <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded-sm">hr_core_test</code>.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[11px] uppercase tracking-wider font-bold text-slate-700">Evento a probar:</label>
            <select
              value={testEventKey}
              onChange={(e) => setTestEventKey(e.target.value)}
              disabled={!canEdit || sendingTest}
              data-testid="hr-test-event-select"
              className="text-sm rounded-sm border border-slate-300 px-2 py-1.5 bg-white focus:border-slate-900 focus:outline-none disabled:bg-slate-100"
            >
              {HR_EVENTS.map((evt) => (
                <option key={evt.key} value={evt.key}>{evt.label}{evt.critical ? " (crítico)" : ""}</option>
              ))}
            </select>
            <Button
              onClick={sendTestEmail}
              disabled={!canEdit || sendingTest || !user?.email}
              variant="outline"
              data-testid="hr-test-send-btn"
              className="rounded-sm border-amber-500 text-amber-800 hover:bg-amber-100"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {sendingTest ? "Enviando…" : "Enviar prueba"}
            </Button>
          </div>
          {testResult && (
            <div
              className={`rounded-sm border p-3 text-xs ${
                testResult.ok
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
              data-testid="hr-test-result"
            >
              <div className="flex items-center gap-2">
                {testResult.ok ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                <strong>
                  {testResult.ok ? "Email de prueba enviado correctamente." : "El proveedor reportó un problema."}
                </strong>
              </div>
              <ul className="mt-2 space-y-1 ml-6 list-disc">
                <li>Evento simulado: <code className="bg-white/60 px-1 py-0.5 rounded-sm">{testResult.event_key}</code> — {testResult.event_label}</li>
                <li>Destinatario real (solo tú): <span className="font-mono">{testResult.sent_to}</span></li>
                <li>Asunto: <em>{testResult.subject}</em></li>
                <li>
                  En una solicitud real habría llegado a:{" "}
                  {testResult.would_have_received?.length ? (
                    <span className="font-mono">{testResult.would_have_received.map((r) => r.email).join(", ")}</span>
                  ) : (
                    <span className="italic text-slate-500">— sin destinatarios resolubles —</span>
                  )}
                </li>
                <li>
                  Auditoría: <code className="bg-white/60 px-1 py-0.5 rounded-sm">{testResult.audit_module}</code>
                  {" / "}
                  <code className="bg-white/60 px-1 py-0.5 rounded-sm">{testResult.audit_event}</code>
                </li>
              </ul>
            </div>
          )}
        </div>
      </Card>

      {/* Tip */}
      <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex gap-2">
        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Las solicitudes <strong>nuevas</strong> usarán la configuración vigente al momento de su creación. Las <strong>existentes</strong> conservan su cálculo original — no se recalculan retroactivamente.</span>
      </div>

      {/* ── Iter 186 (Parte 2) — Diálogo Vista Previa Destinatarios ── */}
      <Dialog
        open={recipientsDialog.open}
        onOpenChange={(o) => setRecipientsDialog((d) => ({ ...d, open: o }))}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="hr-recipients-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Vista previa de destinatarios RRHH
            </DialogTitle>
            <DialogDescription>
              Simulación sin envío real. Revisa qué cuentas recibirían cada evento antes de guardar.
            </DialogDescription>
          </DialogHeader>
          {recipientsDialog.loading && (
            <p className="text-sm text-slate-500 py-6 text-center">Calculando destinatarios…</p>
          )}
          {!recipientsDialog.loading && recipientsDialog.data && (
            <PreviewBody data={recipientsDialog.data} />
          )}
          <DialogFooter>
            <Button
              onClick={() => setRecipientsDialog((d) => ({ ...d, open: false }))}
              variant="outline"
              data-testid="hr-recipients-preview-close-btn"
              className="rounded-sm"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Iter 186 (Parte 2) — Body del diálogo de vista previa ──
function PreviewBody({ data }) {
  const blockedCount = (data.events || []).filter((e) => e.blocked).length;
  return (
    <div className="space-y-4 text-sm">
      {/* Resumen global */}
      <div className={`rounded-sm border p-3 text-xs ${
        !data.global_enabled
          ? "bg-amber-50 border-amber-200 text-amber-800"
          : blockedCount > 0
          ? "bg-red-50 border-red-200 text-red-800"
          : "bg-emerald-50 border-emerald-200 text-emerald-800"
      }`} data-testid="hr-recipients-preview-summary">
        {!data.global_enabled ? (
          <span><strong>Notificaciones RRHH desactivadas globalmente.</strong> No se enviará ningún email aunque haya eventos configurados.</span>
        ) : blockedCount > 0 ? (
          <span><strong>Hay {blockedCount} evento(s) con problemas críticos.</strong> No podrás guardar hasta resolverlos.</span>
        ) : (
          <span><strong>Configuración válida.</strong> Todos los eventos críticos tienen destinatarios válidos.</span>
        )}
      </div>

      {/* Empleado de prueba */}
      {data.sample_employee && (
        <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs">
          <strong className="text-slate-700">Empleado de muestra:</strong>{" "}
          <span className="font-medium">{data.sample_employee.name}</span>
          <span className="text-slate-500"> · {data.sample_employee.email || "(sin email)"}</span>
          {data.sample_employee.manager_email && (
            <span className="text-slate-500"> · mánager: {data.sample_employee.manager_email}</span>
          )}
          <p className="text-[11px] text-slate-400 mt-1">
            Para eventos &quot;Empleado / Mánager / Aprobador&quot; se simula la resolución con este empleado.
          </p>
        </div>
      )}

      {/* Cuentas prohibidas */}
      {(data.banned_emails || []).length > 0 && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-800 flex gap-2" data-testid="hr-recipients-banned-info">
          <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Cuentas bloqueadas a nivel sistema:</strong>{" "}
            {data.banned_emails.join(", ")}
            <p className="mt-0.5">Además se excluyen todos los usuarios marcados como <code>is_system_exception=true</code>.</p>
          </div>
        </div>
      )}

      {/* Lista de eventos */}
      <div className="space-y-2">
        {(data.events || []).map((evt) => (
          <div
            key={evt.key}
            className={`rounded-sm border p-3 ${
              evt.blocked
                ? "border-red-300 bg-red-50"
                : !evt.enabled
                ? "border-slate-200 bg-slate-50 opacity-70"
                : "border-slate-200 bg-white"
            }`}
            data-testid={`hr-recipients-preview-event-${evt.key}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <strong className="text-slate-900">{evt.label}</strong>
                  {evt.critical && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-200">
                      Crítico
                    </span>
                  )}
                  {!evt.enabled && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-600 border border-slate-300">
                      Desactivado
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-200">
                    → {evt.to}
                  </span>
                </div>
                {evt.subject_preview && (
                  <p className="text-[11px] text-slate-500 mt-1 italic">&quot;{evt.subject_preview}&quot;</p>
                )}
              </div>
            </div>
            <div className="mt-2 space-y-1">
              {(evt.recipients || []).length === 0 ? (
                <p className="text-xs text-red-700 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Sin destinatarios resolubles.
                </p>
              ) : (
                evt.recipients.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 text-xs px-2 py-1 rounded-sm ${
                      r.blocked ? "bg-red-100 text-red-800" : "bg-slate-50 text-slate-700"
                    }`}
                    data-testid={`hr-recipients-preview-rcpt-${evt.key}-${i}`}
                  >
                    {r.blocked ? (
                      <ShieldAlert className="w-3 h-3 text-red-600" />
                    ) : (
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    )}
                    <span className="font-mono">{r.email}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">
                      [{r.kind}]
                    </span>
                    {r.warning && <span className="text-[11px] italic">— {r.warning}</span>}
                  </div>
                ))
              )}
              {(evt.warnings || []).map((w, i) => (
                <p key={i} className="text-[11px] text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {w}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
